import { css } from "@emotion/react";
import { useTranslations } from "next-intl";
import { useEffect, useRef, useState } from "react";

import {
  ColorName,
  Colors,
  Popover,
  Text,
  Tooltip,
  TooltipFocusableTrigger,
  Typography,
} from "@zapier/design-system";

import HeldStatusTip from "@zapier/reporting/src/components/global/HeldStatusTip";
import RunStatusIcon from "@zapier/reporting/src/components/global/RunStatusIcon/RunStatusIcon";
import { getStatusValues } from "@zapier/reporting/src/constants";
import {
  HeldReason,
  RunStatus as RunStatusType,
} from "@zapier/reporting/src/graphql-types";

type Props = {
  heldReason?: HeldReason | null;
  heldStaleAuth?: string | null;
  id: string;
  status: RunStatusType;
};

const Styles = {
  info: css`
    display: grid;
    grid-gap: 2px;
  `,
  root: css`
    align-items: center;
    display: grid;
    grid-gap: 10px;
    grid-template-columns: 35px 1fr;
  `,
  title: (iconColor: ColorName) => css`
    ${Typography.SmallPrint4}
    color: ${Colors[iconColor]};
    text-transform: none;
  `,
};

function RunStatus(props: Props) {
  const t = useTranslations("run-status");
  const status = getStatusValues(
    t,
    props.status,
    props.heldReason,
    props.heldStaleAuth
  );

  const isHeld = props.status === RunStatusType.held;
  const triggerRef = useRef<HTMLDivElement>(null);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const leaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleMouseEnter = () => {
    if (leaveTimer.current) clearTimeout(leaveTimer.current);
    setIsPopoverOpen(true);
  };

  const handleMouseLeave = () => {
    leaveTimer.current = setTimeout(() => setIsPopoverOpen(false), 150);
  };

  useEffect(() => {
    return () => {
      if (leaveTimer.current) clearTimeout(leaveTimer.current);
    };
  }, []);

  const subLabel = "subLabel" in status ? status.subLabel : null;

  const statusCell = (
    <div css={Styles.root}>
      <RunStatusIcon size={35} status={props.status} />
      <div css={Styles.info}>
        <span css={Styles.title(status.iconColor)}>{status.label}</span>
        {subLabel && (
          <Text color="GrayWarm9" type="SmallPrint1">
            {subLabel}
          </Text>
        )}
      </div>
    </div>
  );

  if (isHeld) {
    // DS v10 Tooltip only accepts string content. For held runs we need rich
    // JSX (HeldStatusTip includes reason-specific text and help links), so we
    // use a hover-triggered Popover instead. A short leave-delay lets the user
    // move the mouse into the popover to click the "More info." link without
    // the overlay disappearing.
    //
    // Known limitation: keyboard users cannot tab focus into the Popover to
    // reach the help links. This was also true of the v9 TooltipWrapper — the
    // links were never keyboard-reachable — so this is a pre-existing gap
    // rather than a regression introduced here.
    return (
      <>
        <div
          ref={triggerRef}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onFocus={handleMouseEnter}
          onBlur={handleMouseLeave}
        >
          {statusCell}
        </div>
        <Popover
          color="dark"
          isOpen={isPopoverOpen}
          onOpenChange={(open) => {
            if (!open) setIsPopoverOpen(false);
          }}
          placement="bottom left"
          triggerRef={triggerRef}
        >
          <div onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
            <HeldStatusTip
              reason={props.heldReason || HeldReason.unknown}
              taskId={props.id}
            />
          </div>
        </Popover>
      </>
    );
  }

  return (
    <Tooltip
      content={t("tooltip", {
        hasSubLabel: subLabel ? "true" : "false",
        label: status.label,
        subLabel: subLabel ?? "",
      })}
      allowMultiline={!!subLabel}
      placement="bottom left"
    >
      <TooltipFocusableTrigger>{statusCell}</TooltipFocusableTrigger>
    </Tooltip>
  );
}

export default RunStatus;
